
/**
 * Module dependencies.
 */

import { Task } from 'types/tasks';
import { Text, useImageSources } from '@slyk/design-system';
import { color } from 'react-components/styles';
import { getImageSources, resolveImageUrl } from 'client/core/utils/image';
import { routeTemplate } from 'client/core/utils/routes';
import { theme } from 'styled-tools';
import { useHistory } from 'react-router-dom';
import { useTranslate } from '@slyk/i18n';
import React, { useCallback } from 'react';
import payspaceRoutes from '@slyk/config/payspace-routes';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = Task;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;
`;

/**
 * `ImageButton` styled component.
 */

const ImageButton = styled.button`
  appearance: none;
  border: none;
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgba(25, 28, 30, 0.07);
  cursor: pointer;
  display: flex;
  outline: none;
  padding: 0;
  transition: box-shadow ${theme('animations.defaultTransition')};
  width: 100%;

  &:hover,
  &:focus {
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.22);
  }
`;

/**
 * `Image` styled component.
 */

const Image = styled.img`
  aspect-ratio: 3 / 4;
  border-radius: inherit;
  display: block;
  height: auto;
  object-fit: cover;
  width: 100%;
`;

/**
 * `Information` styled component.
 */

const Information = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

/**
 * `Name` styled component.
 */

const Name = styled(Text).attrs({ size: 'normal' })`
  color: ${color('secondary')};
  font-weight: 700;
  margin-bottom: 8px;
  word-break: break-word;
`;

/**
 * `Price` styled component.
 */

const Price = styled(Text).attrs({ size: 'normal' })`
  color: ${color('nightBlue900')};
  word-break: break-word;
`;

/**
 * Export `TaskItem` component.
 */

export function TaskItem(props: Props): JSX.Element {
  const { amount, id, media, name } = props;
  const { translate } = useTranslate();
  const history = useHistory();
  const imageSources = useImageSources(resolveImageUrl(media), '128x');
  const noImageSources = getImageSources('no-image/product', 'webp');
  const handleProductDetails = useCallback(() => {
    return history.push(routeTemplate(payspaceRoutes.tasks.details, { id }));
  }, [history, id]);

  return (
    <Wrapper>
      <ImageButton
        onClick={handleProductDetails}
        type={'button'}
      >
        <Image
          alt={''}
          src={imageSources?.defaultUrl ?? noImageSources.defaultUrl}
          srcSet={imageSources?.retinaUrl ?? noImageSources.retinaUrl}
        />
      </ImageButton>

      <Information>
        <Name>
          {name ?? translate('common.labels.notAvailable')}
        </Name>

        <Price>
          {amount}
        </Price>
      </Information>
    </Wrapper>
  );
}
